/**
 * Standalone engine that works with VK and the Store
 * and fetches more groups.
 * 
 * Store keys that affect behavior:
 * groups[] array of fetched groups
 * backStepsLimit - number of 4800 steps back from the initial "topGroupIdFixed"
 * isGetMoreForward - allowed to find fresh tip
 * 
 * We use interval convention opposite to traditional [from, to)
 * As we mostly move backwards, from high to low, our conventioln is:
 * (min, max] - we exclude lower bound, include higher bound.
 * 
 * 
 */

import bridge from "@vkontakte/vk-bridge";
import { getServerSavedTop, getTimes } from '@/api';
import { LS_KEY_TOKEN, LS_KEY_TOKEN_EXPIRES, useAppStore } from "@/stores/app";
import rateLimit from "./rateLimiter";

/**
 * @typedef {Object} ExecTip
 * @property {Number} max group_id of current topmost group 
 * @property {Number} calls number of calls performed to find the tip. Max 25
 * @property {Number} delta
 * @property {Array} samples (min, max) pairs in the binary search iterations
 */

const VK_API_VERSION = import.meta.env.VITE_VK_API_VERSION;

const VK_STORED_TIP_NAME = "getTip";
const VK_STORED_TIP_VERSION = 1;

const VK_STORED_COUNTS_RANGE_NAME = "counts_range";
const VK_STORED_COUNTS_RANGE_VERSION = 1;

const VK_SCOPE = "groups"; // requested permissions @see https://dev.vk.com/ru/bridge/VKWebAppGetAuthToken

// Stored procedure capabilities
const batchDepth = 1600; // groups per one execute.counts call

let tokenTimer;
let tokenRetries = 0;

/**
 * Return stored token, or fetch fresh access token via bridge
 * Should be valid for at least 2 more minutes
 * @returns {Promise<string>}
 */
async function getVKToken() {
  const store = useAppStore();

  if (store.token && store.tokenExpires - 2 * 60 > Math.round(Date.now() / 1e3)) {
    // Valid token has enough time

    return store.token;
  }

  return await refreshVKToken();
}

/**
 * Gets a new VK token and start a timer to refresh it when appropriate
 * @returns {Promise<string>} fresh VK token
 */
async function refreshVKToken() {
  const store = useAppStore();

  // TODO: progressive increased timer time
  if (!store.isBridgeActive) {
    if (tokenTimer) {
      clearTimeout(tokenTimer);
    }
    tokenRetries += 1;
    const seconds = 15 * tokenRetries;
    console.warn(`Bridge is inactive, will retry in ${seconds} seconds..`);
    tokenTimer = setTimeout(refreshVKToken, seconds * 1e3);

    return;
  }
  
  try {
    const appId = Number(import.meta.env.VITE_VK_APP_ID);
    const response = await bridge.send("VKWebAppGetAuthToken", {
      app_id: appId,
      scope: VK_SCOPE,
    });

    const { access_token: token, scope, expires } = response;
    console.assert(scope === VK_SCOPE, 'VK token scope')
    console.log('refreshVKToken: %o', { access_token: token, scope, expires });

    // Save
    store.token = token;
    store.tokenExpires = expires;

    localStorage.setItem(LS_KEY_TOKEN, store.token);
    localStorage.setItem(LS_KEY_TOKEN_EXPIRES, store.tokenExpires);
    // TODO: on app start look in LS first

    // start timer to refresh the token
    if (tokenTimer) {
      clearTimeout(tokenTimer);
    }
    
    const expectedEpirationInMs = (store.tokenExpires - 10) * 1e3 - Date.now();
    if (expectedEpirationInMs > 0) {
      tokenTimer = setTimeout(refreshVKToken, expectedEpirationInMs);
    } else {
      console.error('refreshToken bad time calculus: %d', expectedEpirationInMs);
    }

    return store.token;
  } catch(error) {
    console.error(error);
  }
}

/**
 * Backend keeps its track of top group
 * finding one every 15 minutes. 
 * Not precise enough, but a good starting point.
 * 
 * Call backend and update state.
 */
async function fetchTopFromBackend() {
  const store = useAppStore();

  const backendTop = await getServerSavedTop(store.startParams);
  const { group_id: backendTopId, timestamp: backendTimestamp } = backendTop;
  console.log('Backend-stored top: %d at %d', backendTopId, backendTimestamp);
  store.topGroupId = backendTopId;
  store.topGroupIdTs = backendTimestamp;
}

async function fetchTimeReferences() {
  const store = useAppStore();

  const references = await getTimes(store.startParams);

  store.addCreationTimes(references);
}

/**
 * Find current top id by calling a VK stored procedure
 * @param {number} range Initial range to start binary search within.
 */
async function findActualTop(range = 1e5) {
  const store = useAppStore();

  store.isFindingTop = true;
  
  const actualTop = await rateLimit(() => bridge.send('VKWebAppCallAPIMethod', {
    method: `execute.${VK_STORED_TIP_NAME}`,
    params: {
      access_token: store.token,
      func_v: VK_STORED_TIP_VERSION,
      range,
      oid: store.topGroupId - 100, // in case some groups were deleted by now
      v: VK_API_VERSION,
    }
  }));

  /** @type ExecTip */
  const actualTopResponse = actualTop.response;
  const { max: actualTopId } = actualTopResponse;

  const positiveShift = store.topGroupId ? actualTopId - store.topGroupId : 0;
  store.topGroupId = actualTopId;
  store.topGroupIdTs = Date.now();

  if (!store.topGroupIdFixed) {
    store.topGroupIdFixed = actualTopId;
  }

  console.log({positiveShift});
  if (positiveShift > 0) {
    // get more groups to the top
    await countsForward(actualTopId - positiveShift, actualTopId);
  }

  store.isFindingTop = false;
}

/**
 * Fetch group counters forward after updated Top.
 * 
 * Single call to the stored proc provides up to 64 * 25 = 1600 groups.
 * We fetch from top group down to lower ids
 * 
 * @param {number} minGroupId lowest groupo id (excluded)
 * @param {number} maxGroupId highest group id (included)
 */
async function countsForward(minGroupId, maxGroupId) {
  const store = useAppStore();

  const stepsToMake = Math.ceil(Math.abs(maxGroupId - minGroupId) / 1600);
  if (stepsToMake <= 0) {
    return;
  }

  store.isFetchingForward = true;

  const stepPromises = [];
  
  const method = `execute.${VK_STORED_COUNTS_RANGE_NAME}`;
  const paramsMaster = {
    min_id: minGroupId,
    max_id: maxGroupId,
    access_token: store.token,
    v: VK_API_VERSION,
    func_v: VK_STORED_COUNTS_RANGE_VERSION,
  };

  for (let step = 0; step < stepsToMake; step += 1) {
    const max_id = maxGroupId - batchDepth * step;
    const min_id = Math.max(minGroupId, max_id - batchDepth);

    const params = {...paramsMaster, min_id, max_id};

    const fn = () => bridge
      .send('VKWebAppCallAPIMethod', { method, params })
      .catch(error => {
        // TODO: handle error - retry only this range?
        console.error(error);
      });
    
    const stepPromise = rateLimit(fn);
    stepPromise.then(batch => {
      const groupsArray = batch?.response;
      if (groupsArray) {
        store.addGroups(groupsArray);
      } else {
        // TODO: error ?
        console.error('FAILED Forward step #%d: %d - %d', step, min_id, max_id);
      }
    });

    stepPromises.push(stepPromise);
  }

  await Promise.allSettled(stepPromises);

  store.isFetchingForward = false;
}

/**
 * Run the main scenario:
 * 1. get the last saved top group from backend
 * 2. using that as a starting point, find the current top group id
 * 3. start the timer interval to find again the updated current top group id later
 * 4. start collecting group titles/counts back from the topGroup id
 * 
 * This all works as a background task, and should be moved to a webworker perhaps.
 */
async function start() {
  const store = useAppStore();

  try {
    store.isFindingTop = true;

    refreshVKToken()
      .then(() => fetchTopFromBackend())
      .then(() => findActualTop())
      .then(async () => {
        // Assign once this starting point to measure 1600-items steps back
        store.topGroupIdFixed = store.topGroupId;
    
        // start loop of getting backlog..
        store.isFindingTop = false;
    
        await makeBackSteps()
      })
    ;

    // at the same time go get the time references
    fetchTimeReferences();
    
    

  } catch(error) {
    store.errors.push(error);
    console.error(error);
  }
};


/**
 * Create enough promises to match store.backSteps to store.backStepsLimit
 */
async function makeBackSteps() {
  const store = useAppStore();

  const stepsToMake = store.backStepsLimit - store.backSteps;
  if (stepsToMake <= 0) {
    return;
  }

  store.isFetchingBackward = true;

  const stepPromises = [];
  
  const method = `execute.${VK_STORED_COUNTS_RANGE_NAME}`;
  const paramsMaster = {
    min_id: 0,
    max_id: 0,
    access_token: store.token,
    v: VK_API_VERSION,
    func_v: VK_STORED_COUNTS_RANGE_VERSION,
  };

  const maxGroupId = store.topGroupIdFixed - batchDepth * store.backSteps;
  const minGroupId = maxGroupId - batchDepth * stepsToMake;

  for (let step = 0; step < stepsToMake; step += 1) {
    // Step number, 0-based, in 1600-size batches from topGroupIdFixed back into history
    const stepId = store.backSteps + step;
    const max_id = maxGroupId - batchDepth * step;
    const min_id = Math.max(minGroupId, max_id - batchDepth);
    console.log("\tstep id %d: (%d, %d]", stepId, min_id, max_id);
    const params = {...paramsMaster, min_id, max_id};
    // TODO: in case of prolonged execution, store.token might get refreshed.
    // Take it from the store _right before_ actually running the job.

    const fn = () => bridge
      .send('VKWebAppCallAPIMethod', { method, params })
      .catch(error => {
        // TODO: handle error - retry only this range?
        console.error(error);
      });
    
    const stepPromise = rateLimit(fn);
    stepPromise.then(batch => {
      const groupsArray = batch?.response;
      if (groupsArray) {
        store.addGroups(groupsArray);
        store.backSteps += 1;
        // TODO: if any batch failed, this introduces discrepancy as the wrong step might be uncounted..
      } else {
        // TODO: handle error ?
        console.error('step id %d failed', stepId);
      }
    });

    stepPromises.push(stepPromise);
  }

  await Promise.allSettled(stepPromises);

  console.log('Back Batch done');

  store.isFetchingBackward = false;
}



/**
 * Ids of collected groups should be a solid sequence.
 * Find missing one or two ids
 */
// eslint-disable-next-line no-unused-vars
function checkMissing() {
  const store = useAppStore();

  console.group('checkMissing');
  const ids = Array.from(store.groupsMap.keys());
  ids.sort((a, b) => b - a);
  let prev, missing = 0;
  for (const id of ids) {
    if (prev && id + 1 !== prev) {
      if (prev === id + 2) {
        console.log('Missing: %d', id + 1);
        missing += 1;
      } else {
        console.log('Missing %d groups between %d and %d', prev - id - 1, prev, id);
        missing += prev - id - 1;
      }
    }
    prev = id;
  }

  console.log('Total ids: %d, missing: %d', ids.length, missing);
  console.groupEnd();
}


export {
  findActualTop,
  getVKToken,
  makeBackSteps,
  refreshVKToken,
  start,
}
